// GoalRush admin constants — mirrored by name from playaz4playaz-backend `constants/goalRush.ts`
// (GoalRushBounds, GoalRushDefenderPattern). The backend validates the same bounds, so a save
// that passes this form passes the server; change both together.

// Bounds in the unit the form shows. `gapDurationMs` and `bonusVisibleMs` are stored in ms but
// entered in seconds, so their bounds here are seconds (backend: 1000–5000 ms).
export const GOALRUSH_BOUNDS = {
  sessionDurationSec: { min: 60, max: 31536000 },
  targetKicks: { min: 1, max: 10000000 },
  targetGoals: { min: 1, max: 1000000 },
  kickMilestonePoints: { min: 0, max: 10000 },
  goalMilestonePoints: { min: 0, max: 10000 },
  pointsPerKick: { min: 0, max: 1000 },
  pointsPerGoal: { min: 0, max: 10000 },
  pointsPerBonusHit: { min: 0, max: 10000 },
  gapIntervalSec: { min: 20, max: 40 },
  gapDurationMs: { min: 1, max: 5 },
  minGapWidth: { min: 0.5, max: 0.8 },
  bonusIntervalSec: { min: 60, max: 120 },
  bonusVisibleMs: { min: 1, max: 5 }
};

// The session length's limit in the unit it is entered in (min 1 in every unit). The backend
// only sees seconds, so it checks the overall range: 1 minute to 365 days.
export const SESSION_DURATION_UNIT_MAX = { minutes: 60, hours: 60, days: 365 };

// Mirrors backend constants/goalRush.ts GoalRushSessionStatus / GoalRushResetReason.
export const GOALRUSH_SESSION_STATUS = { ACTIVE: 'active', PAUSED: 'paused', ENDED: 'ended' };
export const GOALRUSH_END_REASON = {
  TARGET_REACHED: 'target_reached',
  TARGET_NOT_REACHED: 'target_not_reached',
  ADMIN_OVERRIDE: 'admin_override',
  DISABLED: 'disabled'
};

export const GOALRUSH_DEFENDER_PATTERNS = ['classic', 'sweep', 'oscillate', 'arc'];

// Every editable rule, by its leaf path (the same paths the backend's rules use). `kind` picks
// the input; `bound` names its GOALRUSH_BOUNDS entry; `row` groups fields under a labelled row
// inside their section (Defender gaps, Bonus targets).
//   duration — a number + Minutes/Hours/Days, stored in seconds
//   seconds  — entered in seconds, stored in ms
export const RULE_FIELDS = [
  { path: 'sessionDurationSec', kind: 'duration', bound: 'sessionDurationSec', group: 'session' },
  { path: 'targetKicks', kind: 'int', bound: 'targetKicks', group: 'session' },
  { path: 'targetGoals', kind: 'int', bound: 'targetGoals', group: 'session' },
  { path: 'pointsPerKick', kind: 'int', bound: 'pointsPerKick', group: 'points' },
  { path: 'pointsPerGoal', kind: 'int', bound: 'pointsPerGoal', group: 'points' },
  { path: 'pointsPerBonusHit', kind: 'int', bound: 'pointsPerBonusHit', group: 'points' },
  { path: 'kickMilestonePoints', kind: 'int', bound: 'kickMilestonePoints', group: 'points' },
  { path: 'goalMilestonePoints', kind: 'int', bound: 'goalMilestonePoints', group: 'points' },
  { path: 'defenderPatternId', kind: 'pattern', group: 'others' },
  {
    path: 'others.gapIntervalSec',
    kind: 'int',
    bound: 'gapIntervalSec',
    group: 'others',
    row: 'gaps'
  },
  {
    path: 'others.gapDurationMs',
    kind: 'seconds',
    bound: 'gapDurationMs',
    group: 'others',
    row: 'gaps'
  },
  {
    path: 'others.minGapWidth',
    kind: 'decimal',
    bound: 'minGapWidth',
    group: 'others',
    row: 'gaps'
  },
  {
    path: 'others.bonusIntervalSec',
    kind: 'int',
    bound: 'bonusIntervalSec',
    group: 'others',
    row: 'bonus'
  },
  {
    path: 'others.bonusVisibleMs',
    kind: 'seconds',
    bound: 'bonusVisibleMs',
    group: 'others',
    row: 'bonus'
  }
];

export const RULE_GROUPS = ['session', 'points', 'others'];

// The labelled rows inside a group, in order.
export const RULE_ROWS = ['gaps', 'bonus'];

// The locale key for a leaf path: dots are not allowed inside one i18n segment.
export const fieldKey = (path) => path.replace(/\./g, '_');
